// Live-only provisional speaker labels; batch diarization owns the persisted transcript.
import {
  type LiveTranscriptTurn,
  appendLiveTurn,
  formatHhmmss,
} from "@components/Transcriber/segments";

export type SpeakerWord = {
  text: string;
  startSec: number;
  endSec: number;
  speaker?: string;
};

export type SpeakerLiveTranscriptTurn = LiveTranscriptTurn & {
  key: string;
  speaker?: string;
  speakerConfidence?: number;
  words?: SpeakerWord[];
};

// Speaker labels restart per connection, so "A" after a reconnect may differ.
export type ReconnectDividerTurn = SpeakerLiveTranscriptTurn & {
  divider: true;
};

export function turnKey(connectionId: string, turnOrder: number): string {
  return `${connectionId}:${turnOrder}`;
}

export function appendSpeakerTurn(
  turns: SpeakerLiveTranscriptTurn[],
  turn: {
    key: string;
    text: string;
    startSec: number;
    endSec: number;
    speaker?: string;
    speakerConfidence?: number;
    words?: SpeakerWord[];
  }
): SpeakerLiveTranscriptTurn[] {
  const appended = appendLiveTurn([], turn);
  if (appended.length === 0) return turns;
  const base = appended[0];
  const next: SpeakerLiveTranscriptTurn = {
    ...base,
    key: turn.key,
    ...(turn.speaker ? { speaker: turn.speaker } : {}),
    ...(turn.speakerConfidence !== undefined && turn.speakerConfidence > 0
      ? { speakerConfidence: turn.speakerConfidence }
      : {}),
    ...(turn.words && turn.words.length > 0 ? { words: turn.words } : {}),
  };
  return [...turns, next];
}

const orderInConnection = (key: string): [string, number] | null => {
  const match = /^(.*):(\d+)$/.exec(key);
  return match ? [match[1], Number(match[2])] : null;
};

// A late final (e.g. drained after a stop) goes before any later turn of its own connection.
export function insertSpeakerTurnInOrder(
  turns: SpeakerLiveTranscriptTurn[],
  turn: Parameters<typeof appendSpeakerTurn>[1]
): SpeakerLiveTranscriptTurn[] {
  const [next] = appendSpeakerTurn([], turn);
  if (!next) return turns;
  const own = orderInConnection(turn.key);
  const at = own
    ? turns.findIndex((t) => {
        const other = t.key ? orderInConnection(t.key) : null;
        return other !== null && other[0] === own[0] && other[1] > own[1];
      })
    : -1;
  if (at === -1) return [...turns, next];
  return [...turns.slice(0, at), next, ...turns.slice(at)];
}

export function fullTranscriptOf(turns: SpeakerLiveTranscriptTurn[]): string {
  return turns
    .filter((t) => !isReconnectDivider(t))
    .map((t) => t.text.trim())
    .filter(Boolean)
    .join(" ");
}

export function replaceTurn(
  turns: SpeakerLiveTranscriptTurn[],
  key: string,
  update: (turn: SpeakerLiveTranscriptTurn) => SpeakerLiveTranscriptTurn
): SpeakerLiveTranscriptTurn[] {
  const index = turns.findIndex((t) => t.key === key);
  if (index === -1) return turns;
  const next = [...turns];
  next[index] = update(turns[index]);
  return next;
}

export function appendReconnectDivider(
  turns: SpeakerLiveTranscriptTurn[],
  connectionId: string,
  atSec: number
): SpeakerLiveTranscriptTurn[] {
  // Collapse repeated failed reconnects into one divider.
  const last = turns[turns.length - 1] as ReconnectDividerTurn | undefined;
  if (!last || last.divider) return turns;
  const at = Math.max(0, atSec);
  const divider: ReconnectDividerTurn = {
    timestamp: [at, at],
    timestamp_hhmmss: [formatHhmmss(at), formatHhmmss(at)],
    text: "",
    key: `${connectionId}:divider`,
    divider: true,
  };
  return [...turns, divider];
}

export function isReconnectDivider(
  turn: LiveTranscriptTurn
): turn is ReconnectDividerTurn {
  return (turn as ReconnectDividerTurn).divider === true;
}

// Unattributed (PENDING) words join the current run rather than fragmenting it.
export function groupWordsBySpeaker(
  turn: SpeakerLiveTranscriptTurn
): { speaker?: string; text: string; startSec: number }[] {
  const words = turn.words ?? [];
  const whole = [
    { speaker: turn.speaker, text: turn.text, startSec: turn.timestamp[0] },
  ];
  if (words.length === 0) return whole;

  const runs: { speaker?: string; words: SpeakerWord[] }[] = [];
  for (const word of words) {
    const current = runs[runs.length - 1];
    const speaker = word.speaker ?? current?.speaker ?? turn.speaker;
    if (current && current.speaker === speaker) {
      current.words.push(word);
    } else {
      runs.push({ speaker, words: [word] });
    }
  }

  if (runs.length <= 1) {
    return [{ ...whole[0], speaker: runs[0]?.speaker ?? turn.speaker }];
  }
  return runs.map((run) => ({
    speaker: run.speaker,
    text: run.words.map((w) => w.text).join(" "),
    startSec: run.words[0].startSec,
  }));
}
